<template>
  <div class="custom-facet-list">
    <button
      class="focus:outline-hidden flex items-center mb-4 outline-br-green!"
      @click="open = !open"
      :aria-expanded="open.toString()"
      :aria-controls="`facet-panel-${facet}`"
      :id="`facet-toggle-${facet}`"
      type="button"
      :aria-label="
        $t(`exercises.facets.${facet.replace('.title', '')}`) +
        ' ' +
        (open ? $t('common.collapse') : $t('common.expand'))
      "
    >
      <div class="fluid-base leading-relaxed font-medium uppercase">
        {{ $t(`exercises.facets.${facet.replace('.title', '')}`) }}
      </div>
      <DropdownIndicatorIcon :down="!open" class="block ml-2 h-1.5" />
    </button>

    <Expandable :open="open">
      <div
        class="flex flex-wrap items-center"
        :id="`facet-panel-${facet}`"
        role="region"
        :aria-labelledby="`facet-toggle-${facet}`"
      >
        <template v-if="facetValues && facetValues.length > 0">
          <button
            v-for="item in displayedValues"
            :key="item.value"
            type="button"
            class="px-4 py-1 mx-2 my-1.5 font-medium border rounded-full outline-br-green!"
            :class="buttonClass(item)"
            :disabled="item.count === 0"
            @click="handleRefinement(item)"
            :aria-pressed="isRefined(facet, item.value).toString()"
            :tabindex="item.count === 0 ? -1 : 0"
            :aria-label="
              item.count === 0
                ? item.label + ' ' + $t('common.disabled')
                : item.label + (isRefined(facet, item.value) ? ' ' + $t('common.active') : '')
            "
          >
            {{ item.label }}
          </button>

          <!-- Show more/less button -->
          <button
            v-if="hasMoreValues"
            class="focus:outline-hidden p-2 ml-2 border-0 focus-visible:focus-ring-inside outline-br-green!"
            @click="toggleShowMore"
            type="button"
            :aria-expanded="showingMore.toString()"
            :aria-label="
              $t(showingMore ? 'showLess' : 'showMore') + ' ' + $t(`exercises.facets.${facet.replace('.title', '')}`)
            "
          >
            {{ $t(showingMore ? 'showLess' : 'showMore') }}
          </button>
        </template>
      </div>
    </Expandable>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import DropdownIndicatorIcon from '@/components/icons/DropdownIndicatorIcon';
import Expandable from '@/components/common/Expandable';
import { useAlgoliaSearch } from '@/composables/useAlgoliaSearch';

export default defineNuxtComponent({
  components: {
    Expandable,
    DropdownIndicatorIcon,
  },

  props: {
    facet: {
      type: String,
      required: true,
    },
    initialOpen: {
      type: Boolean,
      default: false,
    },
  },

  async setup(props) {
    const open = ref(props.initialOpen);
    const showingMore = ref(false);
    const limit = ref(5);
    const showMoreLimit = ref(100);

    const { getFacetValues, isRefined, toggleRefinement } = await useAlgoliaSearch();

    // Get facet values from the search composable
    const facetValues = computed(() => {
      return getFacetValues(props.facet) || [];
    });

    // Displayed values depending on show more state
    const displayedValues = computed(() => {
      return showingMore.value
        ? facetValues.value.slice(0, showMoreLimit.value)
        : facetValues.value.slice(0, limit.value);
    });

    // Check if we have more values than the current limit
    const hasMoreValues = computed(() => {
      return facetValues.value.length > limit.value;
    });

    // Toggle show more/less
    const toggleShowMore = () => {
      showingMore.value = !showingMore.value;
    };

    // Handle refinement click
    const handleRefinement = (item) => {
      // Use the composable's toggleRefinement method
      toggleRefinement(props.facet, item.value);
    };

    // Button class based on refinement state
    const buttonClass = (item) => {
      if (item.count === 0) {
        return 'text-gray-900 border-gray-900 cursor-default';
      }
      if (isRefined(props.facet, item.value)) {
        return 'bg-br-green-light text-charcoal';
      }
      return 'text-white border-white hover:bg-putty hover:text-charcoal';
    };

    return {
      open,
      showingMore,
      facetValues,
      displayedValues,
      hasMoreValues,
      toggleShowMore,
      handleRefinement,
      buttonClass,
      isRefined,
    };
  },
});
</script>

<style scoped>
@reference "~/assets/css/main.css";
button.default {
  @apply text-white border-white;
}
button.default:hover {
  @apply bg-putty text-charcoal;
}
button[disabled],
button[disabled]:hover {
  @apply text-gray-900 border-gray-900 cursor-default;
}
</style>
